"use client";

import { Instagram, Facebook, Music } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import React from "react";
export default function Footer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const financingPaths = ["/build", "/service", "/repair", "/renovations", "/inspections"];
  const currentPath = typeof window !== "undefined" ? window.location.pathname.toLowerCase() : "";
  const showFinancing = financingPaths.some(p => currentPath.startsWith(p));
  return <section data-section-id="1630" id="system-footer" className="bg-background text-foreground relative font-default">
        {showFinancing && React.createElement("div", {
      className: "mx-auto max-w-[1440px] px-6 md:px-16 pt-14 relative"
    }, React.createElement("div", {
      className: "flex flex-col md:flex-row items-center justify-between gap-6 rounded-xl border border-primary/40 bg-primary/5 px-6 py-8 md:px-10"
    }, React.createElement("div", {
      className: "flex flex-col gap-2 text-center md:text-left"
    }, React.createElement("p", {
      className: "font-mono text-primary text-[0.75rem] uppercase",
      style: {
        letterSpacing: "0.22em"
      }
    }, "§ Financing"), React.createElement("h3", {
      className: "text-2xl md:text-3xl font-bold text-foreground"
    }, "Swim Now, Pay Later — 0% Financing"), React.createElement("p", {
      className: "text-muted-foreground text-[0.9375rem] max-w-xl"
    }, "See if you qualify in minutes through Hearth. No obligation, and checking your rate won't affect your credit score.")), React.createElement(Link, {
      to: "https://app.gethearth.com/partners/rocket-city-water-wizards/john/apply",
      newTab: true,
      className: "inline-flex items-center justify-center rounded-md bg-primary px-8 py-4 text-base font-medium text-primary-foreground shadow-lg transition-all duration-240 hover:opacity-90 whitespace-nowrap"
    }, "Check Your 0% Rate →")))}
            {}
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-px" style={{
      background: "linear-gradient(to right, hsla(191, 91%, 47%, 0) 0%, hsla(191, 91%, 47%, 0.7) 50%, hsla(191, 91%, 47%, 0) 100%)"
    }} />

      <div ref={ref} className={`mx-auto max-w-[1440px] px-6 md:px-16 py-24 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        {}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {}
          <div className="md:col-span-4 flex flex-col gap-6">
            <Link to="/" aria-label="Rocket City Water Wizards — Home" className="inline-block">
              <WvcLogo className="h-9 w-auto" />
            </Link>

            <p className="font-mono text-[0.75rem] uppercase text-muted-foreground" style={{
            letterSpacing: "0.14em",
            lineHeight: 1.4
          }}>AL LICENSE 2608580 <br />CAGE 18E31 <br />UEI IDXXV8ZJMBADF9 <br />EST 2018</p>

            <p className="font-mono text-[0.75rem] uppercase text-muted-foreground" style={{
            letterSpacing: "0.14em",
            lineHeight: 1.4
          }}>Monday–Friday 8am–4pm, Saturday 9am–1pm, Sunday Closed</p>

            {}
            <div aria-hidden="true" className="h-px w-60 mt-2" style={{
            background: "linear-gradient(to right, hsla(191, 91%, 47%, 0.8) 0%, hsla(191, 91%, 47%, 0) 100%)"
          }} />
          </div>

          {}
          <div className="md:col-span-2 flex flex-col gap-5">
            <h3 className="font-mono text-primary text-[0.75rem] uppercase font-default" style={{
            letterSpacing: "0.22em",
            lineHeight: 1
          }}>
              &sect; 01 / SECTIONS
            </h3>
            <ul className="flex flex-col gap-3">
              {[{
              label: "Build",
              href: "/BuildPage"
            }, {
              label: "Service",
              href: "/ServicePage"
            }, {
              label: "Repair",
              href: "/RepairPage"
            }, {
              label: "Renovations",
              href: "/RenovationsPage"
            }, {
              label: "Inspections",
              href: "/InspectionsPage"
            }, {
              label: "Legal",
              href: "/LegalPage"
            }, {
              label: "Feedback",
              href: "https://rocketcitywaterwizards.com/feedback/"
            }].map((item, i) => <li key={i} data-index={i} className="group/nav">
                  <Link to={item.href} className="text-foreground inline-flex items-center text-[0.9375rem] font-sans transition-all duration-240 hover:text-primary">
                    <span aria-hidden="true" className="mr-0 inline-block h-1.5 w-1.5 rounded-lg bg-primary opacity-0 -translate-x-2 transition-all duration-240 group-hover/nav:opacity-100 group-hover/nav:translate-x-0 group-hover/nav:mr-3" />
                    <span className="transition-all duration-240">{item.label}</span>
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="md:col-span-2 flex flex-col gap-5">
            <h3 className="font-mono text-primary text-[0.75rem] uppercase font-default" style={{
            letterSpacing: "0.22em",
            lineHeight: 1
          }}>
              &sect; 02 / CONNECT
            </h3>
            <div className="flex items-center gap-5">
              <Link to="https://instagram.com/rocketcitywaterwizards" newTab aria-label="Instagram – Water Wizards" className="text-foreground transition-colors duration-240 hover:text-primary">
                <Instagram strokeWidth={1.25} className="h-6 w-6" />
              </Link>
              <Link to="https://facebook.com/rocketcitywaterwizards" newTab aria-label="Facebook – Water Wizards" className="text-foreground transition-colors duration-240 hover:text-primary">
                <Facebook strokeWidth={1.25} className="h-6 w-6" />
              </Link>
              <Link to="https://tiktok.com/@rocketcitywaterwizards" newTab aria-label="TikTok – Water Wizards" className="text-foreground transition-colors duration-240 hover:text-primary">
                <svg strokeWidth={1.25} className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" /></svg>
              </Link>
              <Link to="https://soundcloud.com/rocketcitywaterwizards" newTab aria-label="Music – Water Wizards" className="text-foreground transition-colors duration-240 hover:text-primary">
                <Music strokeWidth={1.25} className="h-6 w-6" />
              </Link>
            </div>
          </div>

          {}
          <div className="md:col-span-2 flex flex-col gap-5">
            <h3 className="font-mono text-primary text-[0.75rem] uppercase font-default" style={{
            letterSpacing: "0.22em",
            lineHeight: 1
          }}>
              &sect; 03 / SUPPORT
            </h3>
            <Link to="tel:+18334209973" className="font-sans italic text-foreground transition-colors duration-240 hover:text-primary" style={{
            fontSize: "clamp(1.25rem, 1.5vw, 1.5rem)",
            fontWeight: 400,
            letterSpacing: "-0.015em",
            lineHeight: 1.15
          }}>833-H20-WZRD (833-420-9973)</Link>
            <Link to="mailto:solutions@rocketcitywaterwizards.com" className="font-mono text-xs uppercase text-muted-foreground hover:text-primary transition-colors duration-240 break-words" style={{
            letterSpacing: "0.14em",
            lineHeight: 1.4
          }}>solutions@rocketcitywaterwizards.com</Link>

            <Button asChild className="mt-2 self-start bg-primary text-primary-foreground border border-primary hover:bg-primary/90 font-mono uppercase group/btn" style={{
            fontSize: "0.75rem",
            letterSpacing: "0.2em",
            padding: "18px 28px",
            height: "auto"
          }}>
              <Link to="#">
                <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-lg bg-primary-foreground opacity-0 -translate-x-3 mr-0 transition-all duration-240 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 group-hover/btn:mr-2" />
                Client Portal
              </Link>
            </Button>
          </div>

          {}
          <div className="md:col-span-2 flex flex-col gap-5">
            <h3 className="font-mono text-primary text-[0.75rem] uppercase font-default" style={{
            letterSpacing: "0.22em",
            lineHeight: 1
          }}>
              &sect; 04 / POLICIES
            </h3>
            <ul className="flex flex-col gap-3">
              {[{
              label: "Terms of Service",
              href: "/LegalPage#s01"
            }, {
              label: "Payment Terms",
              href: "/LegalPage#s02"
            }, {
              label: "Refund Policy",
              href: "/LegalPage#s08"
            }, {
              label: "SMS Consent",
              href: "/LegalPage#s10"
            }, {
              label: "Privacy Policy",
              href: "/LegalPage#s11"
            }, {
              label: "Cookie Policy",
              href: "/LegalPage#s12"
            }, {
              label: "Accessibility",
              href: "/LegalPage#s12"
            }].map((policy, i) => <li key={i} data-index={i} className="group/nav">
                  <Link to={policy.href} className="text-foreground inline-flex items-center text-[0.9375rem] font-sans transition-all duration-240 hover:text-primary">
                    <span aria-hidden="true" className="mr-0 inline-block h-1.5 w-1.5 rounded-lg bg-primary opacity-0 -translate-x-2 transition-all duration-240 group-hover/nav:opacity-100 group-hover/nav:translate-x-0 group-hover/nav:mr-3" />
                    <span className="transition-all duration-240">{policy.label}</span>
                  </Link>
                </li>)}
            </ul>
          </div>
        </div>

        {}
        <div className="mt-12 flex justify-center">
          <Button asChild className="px-6 py-2 text-sm font-semibold text-slate-900 bg-cyan-500 hover:bg-cyan-400 transition-colors duration-200">
            <Link to="#">START HERE</Link>
          </Button>
        </div>

        {}
        <div aria-hidden="true" className="mt-12 h-px w-full" style={{
        background: "linear-gradient(to right, hsla(191, 91%, 47%, 0) 0%, hsla(191, 91%, 47%, 0.5) 50%, hsla(191, 91%, 47%, 0) 100%)"
      }} />

        {}
        <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-mono text-muted-foreground text-[0.6875rem] uppercase" style={{
          letterSpacing: "0.18em",
          lineHeight: 1
        }}>© 2026 Rocket City Water Wizards LLC. All rights reserved.</p>
          <p className="font-mono text-muted-foreground text-[0.6875rem] uppercase inline-flex items-center gap-2" style={{
          letterSpacing: "0.18em",
          lineHeight: 1
        }}>
            <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-lg bg-primary" />
            THEIR BEST IS OUR BASELINE
          </p>
        </div>
      </div>
    </section>;
}